import React, { useRef, Suspense, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import {
  Float,
  MeshTransmissionMaterial,
  Environment,
  Lightformer,
  ContactShadows,
  Sphere,
} from '@react-three/drei';

const GlassModel = ({ scale = 1, position = [0, 0, 0] }) => {
  const mesh = useRef();

  useFrame((state, delta) => {
    if (mesh.current) {
      mesh.current.rotation.y += delta * 0.15;
      mesh.current.rotation.x += delta * 0.10;
    }
  });

  return (
    <group scale={scale} position={position}>
      <Float speed={2} rotationIntensity={0.6} floatIntensity={1.2}>
        <mesh ref={mesh}>
          {/* Optimized geometry: 128, 32 is plenty for mobile/web but much lighter than 256, 64 */}
          <torusKnotGeometry args={[1.2, 0.38, 128, 32]} />
          <MeshTransmissionMaterial
            backside
            samples={2} // Reduced from 4 for mobile performance
            resolution={512} // Cap resolution for the refraction buffer
            thickness={0.8}
            chromaticAberration={0.5}
            anisotropy={0.2}
            distortion={0.3}
            distortionScale={0.4}
            temporalDistortion={0.1}
            iridescence={1}
            iridescenceIOR={1}
            iridescenceThicknessRange={[0, 1400]}
            color="#00e5ff"
            transparent
            opacity={0.9}
          />
        </mesh>

        <Sphere args={[0.7, 32, 32]}>
          <meshStandardMaterial
            color="#7c3aed"
            emissive="#7c3aed"
            emissiveIntensity={2}
            wireframe
            transparent
            opacity={0.8}
          />
        </Sphere>

        <Sphere args={[0.4, 32, 32]}>
          <meshBasicMaterial color="#ffffff" />
        </Sphere>
      </Float>
    </group>
  );
};

function Scene() {
  const [scale, setScale] = useState(1.15);
  const [position, setPosition] = useState([0, 0, 0]);

  useEffect(() => {
    const updateScene = () => {
      const width = window.innerWidth;
      if (width > 1200) {
        setScale(1.55);
        setPosition([3.3, 0, 0]);
      } else if (width > 1024) {
        setScale(1.5);
        setPosition([2.8, 0, 0]);
      } else if (width > 768) {
        setScale(1.55);
        setPosition([0, 0.8, 0]);
      } else if (width > 480) {
        setScale(1.5);
        setPosition([0, 0.75, 0]);
      } else {
        setScale(1.45);
        setPosition([0, 0.7, 0]);
      }
    };

    const timer = setTimeout(updateScene, 50);
    window.addEventListener('resize', updateScene);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateScene);
    };
  }, []);

  return (
    <>
      <ambientLight intensity={0.6} />
      <spotLight position={[10, 10, 10]} angle={0.15} intensity={2} color="#00e5ff" />
      <spotLight position={[-10, -10, -10]} angle={0.15} intensity={2} color="#7c3aed" />

      <Suspense fallback={null}>
        <GlassModel scale={scale} position={position} />

        <Environment files="/hdri/potsdamer_platz_1k.hdr">
          <group rotation={[Math.PI / 2, 0, 0]}>
            <Lightformer intensity={4} rotation-y={Math.PI / 2} position={[-5, 1, -1]} scale={[20, 0.2, 1]} />
            <Lightformer intensity={5} position={[0, -1, -5]} scale={[0.1, 20, 1]} color="#00e5ff" />
            <Lightformer intensity={5} position={[0, 1, 5]} scale={[0.1, 20, 1]} color="#7c3aed" />
          </group>
        </Environment>

        <ContactShadows position={[0, -3.5, 0]} opacity={0.6} scale={15} blur={2.5} far={4} color="#00e5ff" />
      </Suspense>
    </>
  );
}

export default function HeroCanvas3D() {
  return (
    <Canvas
      camera={{ position: [0, 0, 10], fov: 45 }}
      // Performance optimization: Adaptive DPR (1 for mobile, up to 1.5 for desktop)
      // High DPR (2+) is extremely expensive on mobile GPUs
      dpr={typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : [1, 1.5]}
      gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      style={{ width: '100%', height: '100%', pointerEvents: 'none' }}
    >
      <Scene />
    </Canvas>
  );
}
